import React, { useContext, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Button, Form, Modal } from 'react-bootstrap';
import { BiPlusCircle } from 'react-icons/bi';
import { LoaderContext } from '../../../../../App';
import MaterialIssueService from '../../../../../core/service/MaterialIssueService';
import CompanyService from '../../../../../core/service/CompanyService';
import QuestionService from '../../../../../core/service/QuestionService';
import { MaterialCategoryList } from '../../../../../core/constants/common';
import '../MaterialityAssessment.scss';

const MaterialIssuesLibraryModal = ({ 
    t, 
    show, 
    onHide, 
    action, 
    topicData, 
    parentTopic, 
    parentSubTopic,
    topics,
    onSuccess,
    filter
}) => {
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [validated, setValidated] = useState(false);
    const [languages, setLanguages] = useState([]);
    const [topicFields, setTopicFields] = useState([
        {
            language: "",
            name: "",
            description: ""
        }
    ]);
    const [primaryLanguage, setPrimaryLanguage] = useState("en");
    const [additionalFields, setAdditionalFields] = useState({
        code: "",
        sortOrder: 0,
        issueCategory: "GENERAL",
        category: "ENVIRONMENTAL"
    });
    const [matIssueCategoryList, setMatIssueCategoryList] = useState([]);

    useEffect(() => {
        if (show) {
            getMatIssuesCategory();
            getLanguages();
        }
    }, [show, action, topicData, parentTopic, parentSubTopic, topics, filter]);

    useEffect(() => {
        if (show && primaryLanguage && languages.length > 0) {
            initializeForm();
        }
    }, [show, primaryLanguage, languages, action, topicData, parentTopic, parentSubTopic, topics]);

    const getMatIssuesCategory = () => {
        MaterialIssueService.getMatIssuesCategory()
            .then(res => {
                if (res.status !== 200) return;
                setMatIssueCategoryList(res.data.data ?? []);
            })
            .catch(err => {
                console.error('Error fetching categories:', err);
            });
    };

    const getLanguages = () => {
        CompanyService.getLanguageList()
            .then((response) => {
                setLanguages([...response.data.data]);
            })
            .catch((error) => {
                console.error('Error fetching languages:', error);
            });
        
        // Get company's primary language
        CompanyService.getCompanyLanguageList()
            .then((response) => {
                if (response.status === 200) {
                    const primaryLang = response.data.find(lang => lang.primary === true);
                    if (primaryLang) {
                        console.log('Primary language found:', primaryLang.code);
                        setPrimaryLanguage(primaryLang.code);
                    } else {
                        console.warn('No primary language found, using fallback: en');
                        setPrimaryLanguage('en');
                    }
                }
            })
            .catch((error) => {
                console.error('Error fetching company languages:', error);
                console.warn('Using fallback language: en');
                setPrimaryLanguage('en');
            });
    };

    const initializeForm = () => {
        console.log('Initializing form with primary language:', primaryLanguage);
        if (action === 'EDIT_TOPIC' || action === 'EDIT_SUBTOPIC' || action === 'EDIT_SUBSUBTOPIC') {
            updateTopicFieldsForModal(topicData);
        } else {
            // Only set one field for UI - the primary language
            setTopicFields([{ language: primaryLanguage, name: "", description: "" }]);
            const generatedCode = generateDynamicCode();
            const generatedSortOrder = generateDynamicSortOrder();
            const companyInfo = JSON.parse(localStorage.getItem('companyInfo'));
            const issueCategory = companyInfo?.materialTopicCategory || "GENERAL";
            console.log('Setting issue category from company info:', issueCategory);
            
            // For new topics, use filter category if available, otherwise use default
            // For subtopics and subsubtopics, use parent's category
            let category;
            if (action === 'ADD_TOPIC' && filter?.category) {
                category = filter.category;
                console.log('Setting category from filter:', category);
            } else {
                category = getCategory();
                console.log('Setting category from parent or default:', category);
            }
            
            setAdditionalFields({ 
                code: generatedCode, 
                sortOrder: generatedSortOrder, 
                issueCategory: issueCategory,
                category: category
            });
        }
        setValidated(false);
    };

    const generateDynamicCode = () => {
        const companyRef = JSON.parse(localStorage.getItem('companyInfo'))?.id;
        const companyInfo = JSON.parse(localStorage.getItem('companyInfo'));
        if (!companyRef) return "";

        let count = 0;
        let prefix = "";

        switch (action) {
            case 'ADD_TOPIC':
                prefix = "MIT";
                count = getTopicCount();
                break;
            case 'ADD_SUBTOPIC':
                prefix = "MITS";
                count = getSubTopicCount(parentTopic);
                return `${prefix}_${companyRef}_${parentTopic.id}_${count + 1}`;
            case 'ADD_SUBSUBTOPIC':
                prefix = "MITSS";
                count = getSubSubTopicCount(parentSubTopic);
                return `${prefix}_${companyRef}_${parentSubTopic.id}_${count + 1}`;
            default:
                return "";
        }

        return `${prefix}_${companyRef}_${companyInfo?.materialTopicCategory}_${count + 1}`;
    };

    const generateDynamicSortOrder = () => {
        let count = 0;

        switch (action) {
            case 'ADD_TOPIC':
                count = getTopicCount();
                break;
            case 'ADD_SUBTOPIC':
                count = getSubTopicCount(parentTopic);
                break;
            case 'ADD_SUBSUBTOPIC':
                count = getSubSubTopicCount(parentSubTopic);
                break;
            default:
                return 0;
        }

        return count + 1;
    };

    const getTopicCount = () => {
        if (!topics || !Array.isArray(topics)) return 0;
        return topics.length;
    };

    const getSubTopicCount = (topic) => {
        if (!topic || !topic.children) return 0;
        return topic.children.length;
    };

    const getSubSubTopicCount = (subTopic) => {
        if (!subTopic || !subTopic.children) return 0;
        return subTopic.children.length;
    };

    const updateTopicFieldsForModal = (content) => {
        const updatedTopicFields = [];

        if (content.localizedContents && content.localizedContents.length > 0) {
            content?.localizedContents.forEach((item) => {
                const { languageCode, fieldName, translatedValue } = item;

                let topicField = updatedTopicFields.find(field => field.language === languageCode);

                if (!topicField) {
                    topicField = { language: languageCode, name: "", description: "" };
                    updatedTopicFields.push(topicField);
                }

                if (fieldName === "name") {
                    topicField.name = translatedValue;
                } else if (fieldName === "description") {
                    topicField.description = translatedValue;
                }
            });

            updatedTopicFields.sort((a, b) => (a.language === primaryLanguage ? -1 : b.language === primaryLanguage ? 1 : 0));
            // Only show the primary language field in UI
            const primaryField = updatedTopicFields.find(field => field.language === primaryLanguage) || updatedTopicFields[0];
            setTopicFields([primaryField]);
            const companyInfo = JSON.parse(localStorage.getItem('companyInfo'));
            const issueCategory = content.issueCategory || companyInfo?.materialTopicCategory || "GENERAL";
            setAdditionalFields({ 
                code: content.code || "", 
                sortOrder: content.sortOrder || 0, 
                issueCategory: issueCategory,
                category: content.category || "ENVIRONMENTAL"
            });
        } else {
            setTopicFields([{ language: primaryLanguage, name: content.name || "", description: content.description || "" }]);
            const companyInfo = JSON.parse(localStorage.getItem('companyInfo'));
            const issueCategory = content.issueCategory || companyInfo?.materialTopicCategory || "GENERAL";
            setAdditionalFields({ 
                code: content.code || "", 
                sortOrder: content.sortOrder || 0, 
                issueCategory: issueCategory,
                category: content.category || "ENVIRONMENTAL"
            });
        }
    };

    const upsertTopic = async (e) => {
        e.preventDefault();

        if (topicFields.some(field => !field.language || !field.name.trim() || !field.description.trim())) {
            setValidated(true);
            return;
        }

        // Start auto-translation process (background task)
        setLoaderDisplay(true);

        try {
            const translatedFields = await translateToAllLanguages();
            await submitTopic(translatedFields);
        } catch (error) {
            console.error('Translation or submission error:', error);
            setLoaderDisplay(false);
            toast.error(t('ErrorOccurred.label'), {
                position: toast.POSITION.TOP_RIGHT,
            });
        }
    };

    const translateToAllLanguages = async () => {
        // Only use the first field (primary language) for translation
        const primaryEntry = topicFields[0];
        if (!primaryEntry || !primaryEntry.language || !primaryEntry.name || !primaryEntry.description) {
            throw new Error('No valid primary language entry found');
        }

        const inputText = [primaryEntry.name, primaryEntry.description];
        const translatedFields = [{ ...primaryEntry }]; // Start with primary language

        // Get all available languages except primary language
        const targetLanguages = languages.filter(lang => lang.code !== primaryLanguage);

        // Translate to each language
        for (const targetLang of targetLanguages) {
            try {
                const response = await QuestionService.translateText(inputText, primaryLanguage, targetLang.code);
                if (response.status === 200 && response.data.translations) {
                    const translations = response.data.translations;
                    translatedFields.push({
                        language: targetLang.code,
                        name: translations[0]?.text || primaryEntry.name,
                        description: translations[1]?.text || primaryEntry.description
                    });
                }
            } catch (error) {
                console.warn(`Translation failed for ${targetLang.code}:`, error);
                // Add original text if translation fails
                translatedFields.push({
                    language: targetLang.code,
                    name: primaryEntry.name,
                    description: primaryEntry.description
                });
            }
        }

        return translatedFields;
    };

    const submitTopic = async (translatedFields) => {
        const primaryEntry = translatedFields.find(field => field.language === primaryLanguage) || translatedFields[0];
        const companyRef = JSON.parse(localStorage.getItem('companyInfo'))?.id;
        
        const payload = {
            id: (action.includes('EDIT')) ? topicData.id : null,
            companyRef: companyRef,
            code: additionalFields.code || "",
            sortOrder: additionalFields.sortOrder || 0,
            issueCategory: additionalFields.issueCategory || "GENERAL",
            name: primaryEntry?.name || "",
            description: primaryEntry?.description || "",
            parentId: getParentId(),
            category: getCategory(), // Always use parent's category for subtopics/subsubtopics
            localizedContents: translatedFields.map(field => ([
                {
                    fieldName: "name",
                    languageCode: field.language,
                    translatedValue: field.name
                },
                {
                    fieldName: "description",
                    languageCode: field.language,
                    translatedValue: field.description
                }
            ])).flat()
        };

        const response = await MaterialIssueService.addTopic(payload);
        // setLoaderDisplay(false);
        
        if (response.status !== 200) {
            setLoaderDisplay(false);
            throw new Error('API call failed');
        }

        toast.success(t('DataAddedSuccessfully.label'), {
            position: toast.POSITION.TOP_RIGHT,
        });

        onSuccess();
        handleClose();
    };

    const getParentId = () => {
        if (action === 'ADD_SUBTOPIC' || action === 'EDIT_SUBTOPIC') {
            return parentTopic?.id || 0;
        } else if (action === 'ADD_SUBSUBTOPIC' || action === 'EDIT_SUBSUBTOPIC') {
            return parentSubTopic?.id || 0;
        }
        return null;
    };

    const getCategory = () => {
        if (action === 'ADD_SUBTOPIC' || action === 'EDIT_SUBTOPIC') {
            return parentTopic?.category || 'ENVIRONMENTAL';
        } else if (action === 'ADD_SUBSUBTOPIC' || action === 'EDIT_SUBSUBTOPIC') {
            return parentSubTopic?.category || 'ENVIRONMENTAL';
        } else if (action === 'ADD_TOPIC') {
            return additionalFields.category || 'ENVIRONMENTAL';
        }
        return topicData?.category || 'ENVIRONMENTAL';
    };

    // Manual translation functions removed - auto-translation on submit only

    const handleInputChange = (index, field, value) => {
        const newFields = [...topicFields];
        newFields[index][field] = value;
        setTopicFields(newFields);
    };

    const handleCategoryChange = (value) => {
        setAdditionalFields({ ...additionalFields, category: value });
    };

    const handleClose = () => {
        setValidated(false);
        setTopicFields([{ language: primaryLanguage, name: "", description: "" }]);
        const companyInfo = JSON.parse(localStorage.getItem('companyInfo'));
        const issueCategory = companyInfo?.materialTopicCategory || "GENERAL";
        setAdditionalFields({ 
            code: "", 
            sortOrder: 0, 
            issueCategory: issueCategory,
            category: "ENVIRONMENTAL" // Default category, will be overridden by getCategory() when needed
        });
        onHide();
    };

    const getModalTitle = () => {
        switch (action) {
            case 'ADD_TOPIC':
                return t("NewTopic.label");
            case 'EDIT_TOPIC':
                return t("UpdateTopic.label");
            case 'ADD_SUBTOPIC':
                return t("AddSubTopic.label");
            case 'EDIT_SUBTOPIC':
                return t("UpdateSubTopic.label");
            case 'ADD_SUBSUBTOPIC':
                return t("AddSubSubTopic.label");
            case 'EDIT_SUBSUBTOPIC':
                return t("UpdateSubSubTopic.label");
            default:
                return t("TopicManagement.label");
        }
    };

    const getTopicLabel = () => {
        if (action === 'ADD_TOPIC' || action === 'EDIT_TOPIC') {
            return t("Topic.label");
        } else if (action === 'ADD_SUBTOPIC' || action === 'EDIT_SUBTOPIC') {
            return t("SubTopic.label");
        } else {
            return t("SubSubTopic.label");
        }
    };

    const isSubTopicOrSubSubTopic = () => {
        return action === 'ADD_SUBTOPIC' || action === 'EDIT_SUBTOPIC' || 
               action === 'ADD_SUBSUBTOPIC' || action === 'EDIT_SUBSUBTOPIC';
    };

    return (
        <Modal 
            show={show}
            size='md'
            onHide={handleClose}
            aria-labelledby="contained-modal-title-vcenter"
            centered
            data-testid="material-issues-library-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title as="h5">
                    {getModalTitle()}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form noValidate validated={validated}>
                    <div className="row m-0">
                    {!isSubTopicOrSubSubTopic() && (
                            <div className="col-lg-12 p-2">
                                <label className="mb-1">{t("Category.label")} <span className="text-danger">*</span></label>
                                <select
                                    className="form-select"
                                    value={additionalFields.category}
                                    onChange={(e) => handleCategoryChange(e.target.value)}
                                    disabled={action.includes('EDIT')}
                                    style={action.includes('EDIT') ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
                                >
                                    {MaterialCategoryList({ t }).map(c => (
                                        <option value={c.value} key={c.key}>{c.text}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className='col-lg-12 p-2'>
                            <label className="mb-1">
                                {getTopicLabel()} <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                value={topicFields[0]?.name || ""}
                                onChange={(e) => handleInputChange(0, "name", e.target.value)}
                                required
                                maxLength={250}
                            />
                        </div>
                        <div className='col-lg-12 p-2'>
                            <label className="mb-1">{t("Description.label")} <span className="text-danger">*</span></label>
                            <textarea
                                className="form-control"
                                value={topicFields[0]?.description || ""}
                                onChange={(e) => handleInputChange(0, "description", e.target.value)}
                                required
                                maxLength={2000}
                                rows={5}
                            />
                        </div>
                       
                       
                    </div>
                    {/* Single language field for UI - only primary language shown */}
                
                    <div className="col-lg-12 p-2 text-center">
                        <button 
                            type="button"
                            className="btn btn-secondary me-2 px-3"
                            onClick={handleClose}
                        >
                            {t("Discard.label")}
                        </button>
                        <button 
                            type="button"
                            className="btn btn-primary px-4" 
                            onClick={upsertTopic}
                        >
                            {t("Save.label")}
                        </button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default withTranslation()(MaterialIssuesLibraryModal);

